import { useMe } from './MeContext';

export type Role = 'platform-admin' | 'tenant-admin' | 'warehouse-admin' | 'voice-supervisor' | 'voice-operator';

const KNOWN_ROLES: readonly Role[] = [
  'platform-admin', 'tenant-admin', 'warehouse-admin', 'voice-supervisor', 'voice-operator',
];

function isKnownRole(s: string): s is Role {
  return (KNOWN_ROLES as readonly string[]).includes(s);
}

export interface RoleInfo {
  ready: boolean;
  /** Union of all applicative roles in the current tenant (or realm for platform-admin). */
  roles: Role[];
  isPlatformAdmin: boolean;
  isTenantAdmin: boolean;
  isWarehouseAdmin: boolean;
  isVoiceSupervisor: boolean;
  /** true when platform-admin OR tenant-admin */
  isAnyAdmin: boolean;
  /** @deprecated use roles[] — highest-privilege singular for back-compat */
  role: Role | null;
}

export function useRole(): RoleInfo {
  const { state } = useMe();
  if (state.kind !== 'ok') {
    return {
      ready: false,
      roles: [],
      isPlatformAdmin: false,
      isTenantAdmin: false,
      isWarehouseAdmin: false,
      isVoiceSupervisor: false,
      isAnyAdmin: false,
      role: null,
    };
  }

  const me = state.me as {
    role: { applicative: string | null; is_platform_admin: boolean };
    roles?: { applicative: string[] };
  };

  // Prefer the new plural field; fall back to the back-compat singular.
  const allRaw: string[] = me.roles?.applicative ?? (me.role.applicative ? [me.role.applicative] : []);
  const roles = allRaw.filter(isKnownRole) as Role[];

  const isPlatformAdmin = me.role.is_platform_admin === true;
  const isTenantAdmin = roles.includes('tenant-admin');
  const isWarehouseAdmin = roles.includes('warehouse-admin');
  const isVoiceSupervisor = roles.includes('voice-supervisor');

  // Highest-privilege singular — preserved for legacy callers.
  const order: Role[] = [
    'platform-admin', 'tenant-admin', 'warehouse-admin', 'voice-supervisor', 'voice-operator',
  ];
  let role: Role | null = null;
  for (const r of order) {
    if (r === 'platform-admin' ? isPlatformAdmin : roles.includes(r)) {
      role = r;
      break;
    }
  }

  return {
    ready: true,
    roles,
    isPlatformAdmin,
    isTenantAdmin,
    isWarehouseAdmin,
    isVoiceSupervisor,
    isAnyAdmin: isPlatformAdmin || isTenantAdmin,
    role,
  };
}
